
import { useState } from "react";
import { Download, Folder, Lock, InfoIcon } from "lucide-react";
import { FaTiktok } from "react-icons/fa";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useUrlValidation } from "@/hooks/useUrlValidation";
import { useIsMobile } from "@/hooks/use-mobile";

const CallToAction = () => {
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { validateTikTokUrl } = useUrlValidation();
  const isMobile = useIsMobile();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url.trim()) {
      toast({
        variant: "destructive",
        title: "Please enter a URL",
        description: "Enter a TikTok collection URL to continue."
      });
      return;
    }
    
    if (!validateTikTokUrl(url)) {
      toast({
        variant: "destructive",
        title: "Invalid URL format",
        description: "Please enter a valid TikTok collection URL."
      });
      return;
    }
    
    setIsLoading(true);
    
    // Store URL in localStorage for potential recovery
    if (typeof window !== "undefined") {
      (window as any).lastTikTokCollectionUrl = url;
    }
    
    // Blur input field to dismiss mobile keyboard
    if (isMobile) {
      (document.activeElement as HTMLElement)?.blur();
      // Add delay for mobile to ensure keyboard is dismissed
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    // Navigate to scan page with the URL
    navigate(`/scan?url=${encodeURIComponent(url)}`);
  };
  
  return (
    <section className="py-24 bg-gradient-to-b from-[#0A0B14] to-[#12141F] relative overflow-hidden">
      <div className="absolute inset-0 opacity-50">
        <div className="absolute top-8 left-16 animate-float-slow">
          <FaTiktok className="w-32 h-32 text-gray-700/10" />
        </div>
        <div className="absolute top-1/4 right-24 animate-float-medium">
          <Folder className="w-24 h-24 text-gray-700/10" />
        </div>
        <div className="absolute bottom-32 left-32 animate-float-fast">
          <Lock className="w-20 h-20 text-gray-700/10" />
        </div>
        <div className="absolute bottom-1/4 right-48 animate-float-medium">
          <Download className="w-28 h-28 text-gray-700/10" />
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
        <h2 className="text-5xl font-bold text-white mb-6 tracking-tight">
          Get Your TikTok Collections Organized
        </h2>
        <p className="text-gray-300 mb-12 text-xl leading-relaxed max-w-3xl mx-auto">
          Stop losing time trying to save TikTok videos one-by-one. Paste your link and get them all at once — with TokCollection.
        </p>
        
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-3xl mx-auto mb-8">
          <Input 
            placeholder="Paste your collection link to begin..." 
            className="bg-[#1A1E2C]/50 border-gray-700/50 text-white h-12 text-lg placeholder:text-gray-400"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={isLoading}
          />
          <Button 
            type="submit"
            size="lg" 
            className="bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#151820] text-lg font-medium whitespace-nowrap px-8"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="animate-pulse">Processing...</span>
              </>
            ) : (
              <>
                Start Now
              </>
            )}
          </Button>
        </form>
        
        <div className="flex items-center justify-center gap-2 text-gray-400">
          <InfoIcon className="w-4 h-4" />
          <p className="text-sm">
            No account required to get started
          </p>
        </div>
      </div>
    </section>
  );
};

export default CallToAction;
